import { useState, useEffect, type FormEvent } from 'react';
import { useMenu } from '../context/MenuContext';
import { useI18n } from '../i18n';
import { submitReservation, fetchReservationConfig, fetchReservationStatus } from '../api';

const GUESTS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export function ReservationForm() {
  const { business } = useMenu();
  const { t } = useI18n();

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [guests, setGuests] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderUuid, setOrderUuid] = useState<string | null>(null);
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  const [rejectedReason, setRejectedReason] = useState<string | null>(null);

  useEffect(() => {
    fetchReservationConfig()
      .then((cfg) => setEnabled(cfg.enabled))
      .catch(() => setEnabled(false));
  }, []);

  // Poll status
  useEffect(() => {
    if (!orderUuid) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetchReservationStatus(orderUuid!);
        if (cancelled) return;
        setStatus(res.status);
        setRejectedReason(res.rejected_reason);
        if (res.status === 'pending') setTimeout(poll, 5000);
      } catch { /* ignore */ }
    }
    poll();
    return () => { cancelled = true; };
  }, [orderUuid]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!guests || !date || !time || !name || !phone) {
      setError(t('reservation.fillRequired') as any);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitReservation({
        customer_name: name.trim(),
        contact_phone: phone.trim(),
        reservation_date: date,
        reservation_time: time,
        guests: parseInt(guests),
        note: note.trim() || undefined,
      });
      setOrderUuid(res.uuid);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  // Status views
  if (status === 'approved') {
    return (
      <div className="hc-waiting">
        <div className="hc-confirmation-icon">&#10003;</div>
        <h2 className="hc-status-approved">{t('reservation.approved' as any)}</h2>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="hc-waiting">
        <div className="hc-confirmation-icon" style={{ background: '#dc3545' }}>&#10007;</div>
        <h2 className="hc-status-rejected">{t('reservation.rejected' as any)}</h2>
        {rejectedReason && <p>{rejectedReason}</p>}
      </div>
    );
  }
  if (orderUuid) {
    return (
      <div className="hc-waiting">
        <div className="hc-spinner" />
        <h2>{t('reservation.pending' as any)}</h2>
      </div>
    );
  }

  return (
    <div className="hc-reservation">
      {enabled === false && (
        <div className="hc-reservation-banner">
          {t('reservation.notAccepting' as any)}
        </div>
      )}

      <div className="hc-reservation-header">
        <h2>{business?.name || 'Restoran'}</h2>
        <p className="hc-reservation-subtitle">{t('reservation.title' as any)}</p>
      </div>

      <form className="hc-reservation-form" onSubmit={handleSubmit}>
        <div className="hc-form-group">
          <label>{t('reservation.guests' as any)} *</label>
          <select value={guests} onChange={(e) => setGuests(e.target.value)} disabled={!enabled}>
            <option value="">{t('reservation.guestsPlaceholder' as any)}</option>
            {GUESTS_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="hc-form-group">
          <label>{t('reservation.date' as any)} *</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            disabled={!enabled}
          />
        </div>

        <div className="hc-form-group">
          <label>{t('reservation.time' as any)} *</label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            disabled={!enabled}
          />
        </div>

        <div className="hc-form-group">
          <label>{t('reservation.name' as any)} *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!enabled}
          />
        </div>

        <div className="hc-form-group">
          <label>{t('reservation.phone' as any)} *</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+385..."
            disabled={!enabled}
          />
        </div>

        <div className="hc-form-group">
          <label>{t('reservation.note' as any)}</label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('reservation.notePlaceholder' as any)}
            disabled={!enabled}
          />
        </div>

        {error && <div className="hc-form-error">{error}</div>}

        <button
          type="submit"
          className="hc-btn hc-btn-primary hc-btn-block"
          disabled={submitting || !enabled}
        >
          {submitting ? t('reservation.submitting' as any) : t('reservation.submit' as any)}
        </button>
      </form>
    </div>
  );
}
